import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiClient } from '../services/api';

const asObject = (value) => {
  let parsed = value;
  // Accept legacy records where an object was JSON encoded twice.
  for (let attempt = 0; attempt < 2 && typeof parsed === 'string'; attempt += 1) {
    try { parsed = JSON.parse(parsed); } catch { return {}; }
  }
  return parsed && typeof parsed === 'object' ? parsed : {};
};

const copy = async (value) => navigator.clipboard.writeText(value);

const download = (filename, value) => {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(new Blob([value], { type: 'text/x-shellscript' }));
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};

function ClusterDetail() {
  const { clusterId } = useParams();
  const [cluster, setCluster] = useState(null);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loadingPassword, setLoadingPassword] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const response = await apiClient.get(`/clusters/${clusterId}`);
      setCluster(response.data);
      setError('');
    } catch (loadError) {
      setError(loadError.response?.data?.error || 'Unable to load cluster.');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, [clusterId]);

  const revealPassword = async () => {
    setLoadingPassword(true);
    setPasswordError('');
    try {
      const response = await apiClient.get(`/clusters/${clusterId}/password`);
      setPassword(response.data.password || '');
    } catch (passwordLoadError) {
      setPasswordError(passwordLoadError.response?.data?.error || 'Unable to retrieve the DBA password.');
    } finally {
      setLoadingPassword(false);
    }
  };

  const details = useMemo(() => {
    const endpoints = asObject(cluster?.endpoints);
    const shell = asObject(cluster?.buyer_shell);
    const backup = asObject(cluster?.publisher_backup);
    return { endpoints, shell, backup, nodes: shell.nodes || {} };
  }, [cluster]);

  if (error) return <div className="alert alert-error">{error}</div>;
  if (!cluster) return <div className="loading-spinner" />;

  const { endpoints, shell, backup, nodes } = details;
  const publisher = endpoints.publisher_nlb_dns || '';
  const subscriber = endpoints.subscriber_nlb_dns || '';
  const httpsPort = endpoints.ports?.https || 8891;
  const region = cluster.region || 'us-east-1';
  const shellScript = (key, node) => `#!/usr/bin/env bash
set -euo pipefail

ROLE_ARN="${shell.role_arn || node.role_arn || ''}"
EXTERNAL_ID="${shell.external_id || ''}"
REGION="${region}"
CLUSTER="${shell.ecs_cluster || ''}"
SERVICE="${node.service_name || ''}"

SOURCE_ARN=$(aws sts get-caller-identity --query Arn --output text)
case "$SOURCE_ARN" in
  arn:aws:iam::*:root)
    echo "Use IAM user or role credentials from the Buyer account; AWS root credentials cannot assume this role." >&2
    exit 2
    ;;
esac

CREDS=$(aws sts assume-role \\
  --role-arn "$ROLE_ARN" \\
  --role-session-name "virtuoso-${cluster.cluster_id}-${key}" \\
  --external-id "$EXTERNAL_ID" \\
  --query 'Credentials.[AccessKeyId,SecretAccessKey,SessionToken]' \\
  --output text)
read AWS_ACCESS_KEY_ID AWS_SECRET_ACCESS_KEY AWS_SESSION_TOKEN <<< "$CREDS"
export AWS_ACCESS_KEY_ID AWS_SECRET_ACCESS_KEY AWS_SESSION_TOKEN

TASK=$(aws ecs list-tasks --region "$REGION" --cluster "$CLUSTER" \\
  --service-name "$SERVICE" --desired-status RUNNING \\
  --query 'taskArns[0]' --output text)
test -n "$TASK" && test "$TASK" != "None"
aws ecs execute-command --region "$REGION" --cluster "$CLUSTER" --task "$TASK" \\
  --container virtuoso --interactive --command '/bin/bash'`;
  const sellerAccountMatch = (shell.role_arn || '').match(/^arn:aws:iam::([0-9]{12}):role\//);
  const sellerAccountId = sellerAccountMatch ? sellerAccountMatch[1] : '545314153384';
  const buyerShellRolePattern = `arn:aws:iam::${sellerAccountId}:role/tenant/virtuoso-clus-*-buyer-shell`;
  const buyerIamPolicy = `{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Sid": "AssumeVirtuosoSaaSClusterBuyerShellRoles",
      "Effect": "Allow",
      "Action": "sts:AssumeRole",
      "Resource": "${buyerShellRolePattern}"
    }
  ]
}`;
  const onlineBackup = `# Run after opening an ECS Exec shell on the Publisher node.
BACKUP_DIR=/opt/virtuoso/database/backup
mkdir -p "$BACKUP_DIR"
/opt/virtuoso/bin/isql localhost:1111 dba "$DBA_PASSWORD" -E <<SQL
delete from DB.DBA.SYS_BACKUP_DIRS;
insert into DB.DBA.SYS_BACKUP_DIRS (bd_id, bd_dir) values (1, '$BACKUP_DIR');
backup_online('virtuoso_#', 10000, 0, vector ('$BACKUP_DIR'));
SQL
echo "Online backup created. The seller-managed backup schedule copies it to durable S3."`;

  return <div className="cluster-detail">
    <div className="page-header">
      <div><Link to="/clusters" className="back-link">Back to RDF Clusters</Link><h1>{cluster.project_name || cluster.cluster_id}</h1><p className="subtitle">{cluster.tenant_module_cluster_plan || cluster.cluster_plan}</p></div>
      <div className="inline-actions"><button className="btn btn-secondary" onClick={load} disabled={refreshing}>{refreshing ? 'Refreshing...' : 'Refresh'}</button>{publisher && <a className="btn btn-primary" href={`https://${publisher}:${httpsPort}/conductor`} target="_blank" rel="noreferrer">Open Publisher Conductor</a>}</div>
    </div>

    <div className="cluster-grid">
      <section className="card"><div className="card-header"><h2>Topology & Status</h2><span className={`badge ${cluster.status === 'running' ? 'badge-success' : 'badge-warning'}`}>{cluster.status}</span></div><dl className="cluster-kv"><dt>Publisher</dt><dd>{cluster.topology?.publisher_cpu / 1024 || '-'} vCPU, {cluster.topology?.publisher_memory_gb || '-'} GB</dd><dt>Subscribers</dt><dd>{cluster.topology?.subscriber_count || '-'} x {cluster.topology?.subscriber_cpu / 1024 || '-'} vCPU, {cluster.topology?.subscriber_memory_gb || '-'} GB</dd><dt>Edition</dt><dd>Virtuoso Commercial</dd><dt>Region</dt><dd>{region}</dd><dt>Agreement</dt><dd><code>{cluster.customer_agreement_id || '-'}</code></dd></dl></section>
      <section className="card"><div className="card-header"><h2>Connection Information</h2></div>{publisher ? <dl className="cluster-kv"><dt>Publisher Conductor</dt><dd><a href={`https://${publisher}:${httpsPort}/conductor`} target="_blank" rel="noreferrer">https://{publisher}:{httpsPort}/conductor</a></dd><dt>Publisher SPARQL</dt><dd><a href={`https://${publisher}:${httpsPort}/sparql`} target="_blank" rel="noreferrer">https://{publisher}:{httpsPort}/sparql</a></dd><dt>Subscriber SPARQL</dt><dd><a href={`https://${subscriber}:${httpsPort}/sparql`} target="_blank" rel="noreferrer">https://{subscriber}:{httpsPort}/sparql</a></dd><dt>TLS SQL / ODBC / JDBC</dt><dd><code>{publisher}:{endpoints.ports?.sql_tls || 1112}</code></dd><dt>Read routing</dt><dd>Use the subscriber endpoint for read-only SPARQL and SQL workloads.</dd></dl> : <p className="muted">Endpoints will appear after provisioning completes.</p>}</section>
    </div>

    <section className="card"><div className="card-header"><h2>Database Credentials</h2></div><p className="muted">The cluster uses the <code>dba</code> account. The password is unique to this cluster and available only to authenticated users of this customer account.</p><dl className="cluster-kv"><dt>TLS isql</dt><dd><code>isql {publisher || '&lt;publisher-host&gt;'}:{endpoints.ports?.sql_tls || 1112} -E dba '&lt;DBA password&gt;'</code></dd><dt>Username</dt><dd><code>dba</code></dd><dt>Password</dt><dd>{password ? <><code>{password}</code> <button className="btn btn-secondary btn-small" onClick={() => copy(password)}>Copy</button></> : <button className="btn btn-secondary" onClick={revealPassword} disabled={loadingPassword}>{loadingPassword ? 'Retrieving...' : 'Reveal DBA Password'}</button>}</dd></dl>{passwordError && <div className="alert alert-error">{passwordError}</div>}</section>

    <section className="card"><div className="card-header"><h2>Container Shell Access</h2></div><p className="muted">Attach the one-time Buyer IAM policy below to the IAM user or role used with the AWS CLI, then run a node script from AWS CLI v2 with the Session Manager plugin installed. AWS root credentials cannot assume roles.</p><div className="cluster-note"><div className="inline-actions"><strong>One-time Buyer IAM setup</strong><button className="btn btn-secondary btn-small" onClick={() => copy(buyerIamPolicy)}>Copy IAM Policy</button></div><p className="muted">This policy covers all current and future Virtuoso RDF Cluster shell roles in this seller account.</p><pre className="command-block command-block-light">{buyerIamPolicy}</pre></div><dl className="cluster-kv"><dt>Role</dt><dd><code>{shell.role_arn || 'Provisioning metadata pending'}</code></dd><dt>External ID</dt><dd><code>{shell.external_id || 'Provisioning metadata pending'}</code></dd></dl><div className="cluster-node-grid">{Object.entries(nodes).map(([key, node]) => <div className="cluster-node" key={key}><h3>{node.label || key}</h3><p><code>{node.service_name}</code></p><div className="inline-actions"><button className="btn btn-secondary btn-small" onClick={() => copy(shellScript(key, node))}>Copy Shell Script</button><button className="btn btn-secondary btn-small" onClick={() => download(`${cluster.cluster_id}-${key}-shell.sh`, shellScript(key, node))}>Download Script</button></div></div>)}</div></section>

    <section className="card"><div className="card-header"><h2>Backup & Recovery</h2></div><p>Publisher online backups are seller-scheduled and copied to a tenant-isolated durable S3 prefix. Buyers may create an additional online backup from a Publisher shell; restore remains seller-controlled to protect replication consistency.</p><dl className="cluster-kv"><dt>Schedule</dt><dd>{backup.schedule_expression || 'Daily seller-managed backup schedule'}</dd><dt>Durable backup location</dt><dd><code>{backup.backup_s3_uri || 'Provisioning metadata pending'}</code></dd><dt>Retention</dt><dd>{backup.retention_days ? `${backup.retention_days} days` : 'Marketplace plan retention policy'}</dd></dl><div className="inline-actions"><button className="btn btn-secondary" onClick={() => copy(onlineBackup)}>Copy Publisher Backup Commands</button></div><pre className="command-block">{onlineBackup}</pre><div className="cluster-note"><strong>Restore request:</strong> contact OpenLink support with cluster ID <code>{cluster.cluster_id}</code>, the required recovery point, and confirmation that write activity can be paused. The seller stops the Publisher, restores the selected backup set, validates replication, then returns the cluster to service.</div></section>
  </div>;
}

export default ClusterDetail;
